import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AudioDeviceService {
  // Device list observables
  private availableAudioInputDevices = new BehaviorSubject<MediaDeviceInfo[]>([]);
  private availableAudioOutputDevices = new BehaviorSubject<MediaDeviceInfo[]>([]);

  // Selected device observables
  private currentAudioInputDevice = new BehaviorSubject<string>('');
  private currentAudioOutputDevice = new BehaviorSubject<string>('');
  private currentRingingDevice = new BehaviorSubject<string>('');

  // Audio input availability tracking
  private audioInputAvailable = new BehaviorSubject<boolean>(true);

  // Capabilities
  private _isAudioOutputSelectionSupported: boolean = 'setSinkId' in HTMLAudioElement.prototype;

  constructor() {
    // Initial device enumeration
    this.enumerateDevices();

    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', () => {
      this.enumerateDevices();
    });

    // Load saved preferences
    this.loadSavedPreferences();
  }

  /**
   * Enumerates available media devices and updates the corresponding BehaviorSubjects
   */
  async enumerateDevices(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      const audioOutputs = devices.filter(device => device.kind === 'audiooutput');

      this.availableAudioInputDevices.next(audioInputs);
      this.availableAudioOutputDevices.next(audioOutputs);

      // Update audio input availability based on device list
      const hasAudioInputDevices = audioInputs.length > 0;
      if (hasAudioInputDevices && !this.audioInputAvailable.value) {
        // Devices became available again - check if selected device is back or default works
        this.audioInputAvailable.next(true);
        console.log('[AudioDeviceService] Audio input device(s) detected');
      } else if (!hasAudioInputDevices && this.audioInputAvailable.value) {
        this.audioInputAvailable.next(false);
        console.log('[AudioDeviceService] No audio input devices detected');
      }

      // Set default devices if none selected
      if (!this.currentAudioInputDevice.value && audioInputs.length > 0) {
        this.currentAudioInputDevice.next('');  // Empty string means system default
      }

      if (!this.currentAudioOutputDevice.value && audioOutputs.length > 0) {
        this.currentAudioOutputDevice.next('');  // Empty string means system default
      }

      if (!this.currentRingingDevice.value && audioOutputs.length > 0) {
        this.currentRingingDevice.next('');  // Empty string means system default
      }
    } catch (error) {
      console.error('Error enumerating media devices:', error);
    }
  }

  /**
   * Loads saved device preferences from localStorage
   */
  private loadSavedPreferences(): void {
    const savedInputId = localStorage.getItem('preferredMicrophoneId');
    const savedOutputId = localStorage.getItem('preferredSpeakerId');
    const savedRingingId = localStorage.getItem('preferredRingingDeviceId');
    
    if (savedInputId !== null) {
      this.currentAudioInputDevice.next(savedInputId);
    }
    
    if (savedOutputId !== null) {
      this.currentAudioOutputDevice.next(savedOutputId);
    }
    
    if (savedRingingId !== null) {
      this.currentRingingDevice.next(savedRingingId);
    }
  }

  /**
   * Save device preference to localStorage
   */
  private savePreference(type: 'input' | 'output' | 'ringing', deviceId: string): void {
    if (type === 'input') {
      localStorage.setItem('preferredMicrophoneId', deviceId);
    } else if (type === 'output') {
      localStorage.setItem('preferredSpeakerId', deviceId);
    } else if (type === 'ringing') {
      localStorage.setItem('preferredRingingDeviceId', deviceId);
    }
  }

  /**
   * Sets the selected audio input device
   */
  setAudioInputDevice(deviceId: string): void {
    this.currentAudioInputDevice.next(deviceId);
    this.savePreference('input', deviceId);
  }

  /**
   * Sets the selected audio output device
   */
  setAudioOutputDevice(deviceId: string): void {
    if (!this.isAudioOutputSelectionSupported) {
      console.warn('Audio output selection is not supported in this browser');
      return;
    }
    
    this.currentAudioOutputDevice.next(deviceId);
    this.savePreference('output', deviceId);
  }
  
  /**
   * Sets the selected ringing tone device
   */
  setRingingDevice(deviceId: string): void {
    if (!this.isAudioOutputSelectionSupported) {
      console.warn('Audio output selection is not supported in this browser');
      return;
    }
    
    this.currentRingingDevice.next(deviceId);
    this.savePreference('ringing', deviceId);
  }

  /**
   * Creates a media stream with the currently selected audio input device.
   * Falls back to system default if the selected device is unavailable.
   * Updates audioInputAvailable state on failure.
   */
  async getAudioStream(withVideo: boolean = false): Promise<MediaStream> {
    const selectedDeviceId = this.currentAudioInputDevice.value;

    // If a specific device is selected (not system default), try it first
    if (selectedDeviceId) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: selectedDeviceId } },
          video: withVideo
        });
        // Device works - ensure availability is set
        if (!this.audioInputAvailable.value) {
          this.audioInputAvailable.next(true);
        }
        return stream;
      } catch (error) {
        if (error instanceof OverconstrainedError || (error instanceof DOMException && error.name === 'OverconstrainedError')) {
          console.warn(`[AudioDeviceService] Selected audio input device '${selectedDeviceId}' unavailable, falling back to system default`);
          // Fall through to try system default
        } else {
          throw error; // Permission denied or other non-device errors
        }
      }
    }

    // Try system default
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: withVideo
      });
      // Default device works - ensure availability is set
      if (!this.audioInputAvailable.value) {
        this.audioInputAvailable.next(true);
      }
      return stream;
    } catch (error) {
      if (error instanceof OverconstrainedError || (error instanceof DOMException && error.name === 'OverconstrainedError')) {
        console.error('[AudioDeviceService] No audio input device available (system default also failed)');
        this.audioInputAvailable.next(false);
      }
      throw error;
    }
  }

  /**
   * Applies the current audio output device to an HTML audio element
   */
  async applyAudioOutputDevice(element: HTMLAudioElement): Promise<void> {
    if (!this.isAudioOutputSelectionSupported || !element) {
      return;
    }
    
    // Skip if no device selected or using default
    if (!this.currentAudioOutputDevice.value) {
      return;
    }
    
    try {
      // Use the setSinkId method
      const sinkId = this.currentAudioOutputDevice.value;
      if (element.setSinkId) {
        await element.setSinkId(sinkId);
      }
    } catch (error) {
      console.error('Error setting audio output device:', error);
    }
  }
  
  /**
   * Applies the current ringing device to an HTML audio element
   */
  async applyRingingDevice(element: HTMLAudioElement): Promise<void> {
    if (!this.isAudioOutputSelectionSupported || !element) {
      return;
    }
    
    // Get the ringing device ID, or fall back to regular output device if not set
    const ringingDeviceId = this.currentRingingDevice.value || this.currentAudioOutputDevice.value;
    
    // Skip if no device selected or using default
    if (!ringingDeviceId) {
      return;
    }
    
    try {
      // Use the setSinkId method
      if (element.setSinkId) {
        await element.setSinkId(ringingDeviceId);
      }
    } catch (error) {
      console.error('Error setting ringing device:', error);
    }
  }

  /**
   * Returns whether audio output selection is supported by the browser
   */
  get isAudioOutputSelectionSupported(): boolean {
    return this._isAudioOutputSelectionSupported;
  }

  // Observable getters
  getAvailableAudioInputDevices(): Observable<MediaDeviceInfo[]> {
    return this.availableAudioInputDevices.asObservable();
  }

  getAvailableAudioOutputDevices(): Observable<MediaDeviceInfo[]> {
    return this.availableAudioOutputDevices.asObservable();
  }

  getCurrentAudioInputDevice(): Observable<string> {
    return this.currentAudioInputDevice.asObservable();
  }

  getCurrentAudioOutputDevice(): Observable<string> {
    return this.currentAudioOutputDevice.asObservable();
  }
  
  getCurrentRingingDevice(): Observable<string> {
    return this.currentRingingDevice.asObservable();
  }

  /**
   * Get current audio input device value (synchronous)
   */
  getCurrentAudioInputDeviceValue(): string {
    return this.currentAudioInputDevice.value;
  }

  /**
   * Mark audio input as unavailable (called externally when getUserMedia fails)
   */
  markAudioInputUnavailable(): void {
    if (this.audioInputAvailable.value) {
      this.audioInputAvailable.next(false);
      console.log('[AudioDeviceService] Audio input marked as unavailable');
    }
  }

  getAudioOutputSelectionSupported(): Observable<boolean> {
    return of(this.isAudioOutputSelectionSupported);
  }

  /**
   * Observable that emits whether audio input is available
   */
  getAudioInputAvailable(): Observable<boolean> {
    return this.audioInputAvailable.asObservable();
  }

  /**
   * Get current audio input availability (synchronous)
   */
  getAudioInputAvailableValue(): boolean {
    return this.audioInputAvailable.value;
  }
}
